import { useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { FaPaperPlane, FaRegCommentDots, FaSpinner, FaTrash } from "react-icons/fa";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { HomeSecondarySection } from "@/components/v2/home/HomeSecondarySection";
import { useAuth } from "@/context/AuthContext";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  addEventComment,
  deleteEventComment,
  fetchEventComments,
  type EventComment,
} from "@/lib/api/eventComments";
import { supabase } from "@/lib/supabase";

interface HomeEventCommentsProps {
  eventId: string;
  groupId: string;
}

export function HomeEventComments({ eventId, groupId }: HomeEventCommentsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { error: toastError, success } = useEnhancedToast();
  const preferReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const [comments, setComments] = useState<EventComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const rows = await fetchEventComments(eventId);
        if (cancelled) return;
        setComments(rows);
      } catch {
        if (!cancelled) {
          toastError({
            title: t("errors.error"),
            description: t("v2.home.eventCommentsLoadError"),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    const channel = supabase
      .channel(`event_comments:${eventId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "event_comments",
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          void load();
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [eventId, t, toastError]);

  useEffect(() => {
    if (comments.length > 0) {
      commentsEndRef.current?.scrollIntoView({ behavior: preferReducedMotion ? "auto" : "smooth" });
    }
  }, [comments.length, preferReducedMotion]);

  const authorLabel = (comment: EventComment) => {
    if (user && comment.user_id === user.id) {
      return (
        (user.user_metadata?.full_name as string | undefined) ??
        user.email?.split("@")[0] ??
        t("v2.home.eventCommentsYou")
      );
    }
    return t("v2.home.eventCommentsMember");
  };

  const authorInitial = (comment: EventComment) => authorLabel(comment).charAt(0).toUpperCase();

  const handleSubmit = async () => {
    if (!user || !draft.trim()) return;
    setSubmitting(true);
    try {
      const created = await addEventComment(eventId, groupId, user.id, draft);
      setComments((prev) => [...prev, created]);
      setDraft("");
      success({
        title: t("v2.home.eventCommentPosted"),
        description: t("v2.home.eventCommentPostedDesc"),
      });
    } catch {
      toastError({
        title: t("errors.error"),
        description: t("v2.home.eventCommentPostError"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteEventComment(commentId);
      setComments((prev) => prev.filter((row) => row.id !== commentId));
    } catch {
      toastError({
        title: t("errors.error"),
        description: t("v2.home.eventCommentDeleteError"),
      });
    }
  };

  return (
    <HomeSecondarySection
      label={t("v2.home.eventCommentsTitle")}
      summary={
        comments.length > 0
          ? t("v2.home.eventCommentsCount", { count: comments.length })
          : t("v2.home.eventCommentsEmptySummary")
      }
      defaultOpen={comments.length > 0}
    >
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-4" aria-busy="true">
            <FaSpinner className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-3 text-muted-foreground text-sm">
            <FaRegCommentDots className="h-6 w-6 mx-auto mb-1 opacity-30" aria-hidden />
            <p>{t("v2.home.eventCommentsEmpty")}</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1" role="log">
            {comments.map((comment) => (
              <article key={comment.id} className="flex gap-2">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage alt="" />
                  <AvatarFallback className="text-xs">{authorInitial(comment)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-semibold text-xs truncate">{authorLabel(comment)}</span>
                      <time
                        dateTime={comment.created_at}
                        className="text-[11px] text-muted-foreground shrink-0"
                      >
                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      </time>
                    </div>
                    {user?.id === comment.user_id && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => void handleDelete(comment.id)}
                        aria-label={t("v2.home.eventCommentDelete")}
                      >
                        <FaTrash className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words">{comment.content}</p>
                </div>
              </article>
            ))}
            <div ref={commentsEndRef} />
          </div>
        )}

        {user ? (
          <div className="space-y-2 border-t border-dashed pt-3">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t("v2.home.eventCommentPlaceholder")}
              className="min-h-[72px] text-sm resize-none"
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                disabled={submitting || !draft.trim()}
                onClick={() => void handleSubmit()}
                className="min-h-[36px]"
              >
                {submitting ? (
                  <FaSpinner className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <FaPaperPlane className="mr-1.5 h-3 w-3" />
                )}
                {submitting ? t("common.loading") : t("v2.home.eventCommentSubmit")}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center">{t("v2.home.eventCommentsLogin")}</p>
        )}
      </div>
    </HomeSecondarySection>
  );
}
